/**
 * 通知中心弹窗组件
 * 点击铃铛图标后弹出，展示通知消息列表
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer, Table, Button, Typography, message, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ClearOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { pageNotificationRecords, markNotificationAsRead, markAllNotificationsAsRead, getUnreadNotificationCount } from '@/services/notification/notificationService';
import type { NotificationRecordDTO } from '@/types/notification/notificationRecord';

const { Text } = Typography;

interface Props {
  visible: boolean;
  onClose: () => void;
  onUnreadCountChange?: (count: number) => void;
}

const NotificationCenter: React.FC<Props> = ({ visible, onClose, onUnreadCountChange }) => {
  const { t } = useTranslation();

  const [dataSource, setDataSource] = useState<NotificationRecordDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [markingAllRead, setMarkingAllRead] = useState(false);

  // 获取全量未读数量
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await getUnreadNotificationCount();
      if (response.success && response.data != null) {
        onUnreadCountChange?.(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  }, [onUnreadCountChange]);

  // 加载通知列表
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await pageNotificationRecords({ currentPage, pageSize });
      if (response.success && response.data) {
        setDataSource(response.data.tableData);
        setTotal(response.data.paging.totalCount);
      }
      // 获取全量未读数量
      await fetchUnreadCount();
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, fetchUnreadCount]);

  useEffect(() => {
    if (visible) {
      fetchData();
    }
  }, [visible, currentPage, pageSize, fetchData]);

  // 标记单条为已读
  const handleRowClick = async (record: NotificationRecordDTO) => {
    if (record.readTime) return; // 已读则不处理
    
    try {
      const response = await markNotificationAsRead(record.notificationId);
      if (response.success) {
        // 更新本地数据
        setDataSource(prev => 
          prev.map(item => 
            item.notificationId === record.notificationId 
              ? { ...item, readTime: Date.now() } 
              : item
          )
        );
        // 重新获取全量未读数量
        await fetchUnreadCount();
      }
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  // 一键已读
  const handleMarkAllRead = async () => {
    setMarkingAllRead(true);
    try {
      const response = await markAllNotificationsAsRead();
      if (response.success) {
        message.success(t('notification.center.markAllReadSuccess'));
        // 更新本地数据
        setDataSource(prev => 
          prev.map(item => ({ ...item, readTime: item.readTime || Date.now() }))
        );
        // 通知父组件更新未读数量
        onUnreadCountChange?.(0);
      }
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      message.error(t('notification.center.markAllReadFailed'));
    } finally {
      setMarkingAllRead(false);
    }
  };

  // 计算当前未读数量
  const unreadCount = dataSource.filter(r => !r.readTime).length;

  const columns: ColumnsType<NotificationRecordDTO> = [
    {
      title: '',
      dataIndex: 'readTime',
      key: 'unreadIndicator',
      width: 40,
      render: (readTime: number | null) => (
        !readTime ? (
          <Tooltip title={t('notification.center.unread')}>
            <span style={{
              display: 'inline-block',
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: '#ff4d4f',
            }} />
          </Tooltip>
        ) : null
      ),
    },
    {
      title: t('notification.center.table.messageTime'),
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      width: 180,
      ellipsis: true,
      render: (time: number) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: t('notification.center.table.eventName'),
      dataIndex: 'title',
      key: 'title',
      width: 200,
      ellipsis: true,
      render: (text: string) => text || '-',
    },
    {
      title: t('notification.center.table.messageContent'),
      dataIndex: 'content',
      key: 'content',
      width: 500,
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text} placement="topLeft">
          <span>{text || '-'}</span>
        </Tooltip>
      ),
    },
  ];

  return (
    <Drawer
      title={t('notification.center.title')}
      open={visible}
      onClose={onClose}
      placement="right"
      width="50vw"
      destroyOnClose
      styles={{
        body: { padding: '16px 24px 24px' },
      }}
    >
      {/* 总数与操作行 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Text type="secondary">{t('notification.center.total', { total })}</Text>
        {unreadCount > 0 && (
          <Button
            type="primary"
            icon={<ClearOutlined />}
            onClick={handleMarkAllRead}
            loading={markingAllRead}
          >
            {t('notification.center.markAllRead')}
          </Button>
        )}
      </div>

      {/* 通知列表表格 */}
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1000 }}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: !record.readTime ? 'pointer' : 'default' },
        })}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          showPrevNextJumpers: true,
          locale: {
            items_per_page: '',
            prev_page: t('notification.center.pagination.prevPage'),
            next_page: t('notification.center.pagination.nextPage'),
          },
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
        }}
      />
    </Drawer>
  );
};

export default NotificationCenter;
